import type { FolderViewModel } from '@mimir/common/dto/folderModels';
import { FolderKind } from '@mimir/common/dto/folderModels';
import { getFolderAncestry } from '@mimir/web/api/globalClientFolder';
import { logErrorAndShowToast } from './uiError';
import { fetchSubfolders, folders as foldersGlobal, getFolderFromCache, getTree, getTreeIncludingRoot } from '@mimir/web/globals/folders';

export const checkIfRoot = (folder: FolderViewModel): boolean => {
  const rootId = foldersGlobal.rootId;
  if (rootId && folder.id === rootId) {
    return true;
  } else if (folder.kind === FolderKind.TenantRoot) {
    return true;
  }

  return false;
};

export const tryToGetFoldersToRootFromCache = (folderId: string, includeTenantSpace = false) => {
  /**
   * This method tries to find a folder's ancestry in the cache using 'getFolderFromCache'.
   * It returns partial or full ancestry of 'FolderViewModels' which is found in the cache.
   * One can determine whether the folders till root are found by checking if it's parent is
   * 'undefined' from the return value
   */
  if (!folderId) return [];
  let childId = folderId;
  const folders: FolderViewModel[] = [];
  let currentFolder = getFolderFromCache(childId);
  while (currentFolder) {
    folders.unshift(currentFolder);
    if ((!includeTenantSpace && checkIfRoot(currentFolder)) || currentFolder.parent === undefined) {
      break;
    }
    childId = currentFolder.parent;
    currentFolder = getFolderFromCache(childId);
  }
  return folders;
};

const fetchAllSubfoldersFromIdList = async (folderIdsList: string[]) => {
  for (const folderId of folderIdsList) {
    const folder = getFolderFromCache(folderId);
    if (folder?.hasSubfolder && !folder?.hasLoadedSubFolders) {
      await fetchSubfolders(folder, undefined);
    }
  }
};


async function getFolderIdsToRoot(rootFolderId: string, includeTenantSpace: boolean): Promise<string[]> {
  let folderId = rootFolderId;
  const folders = tryToGetFoldersToRootFromCache(folderId, includeTenantSpace);
  const folderIdsTillRoot = folders.map(folder => folder.id);
  if (!includeTenantSpace && folders.length > 0 && checkIfRoot(folders[0])) {
    // Get rid of 'Tenant Space'
    folderIdsTillRoot.shift();
    return folderIdsTillRoot;
  }

  if (folderIdsTillRoot.length > 0) {
    folderId = folderIdsTillRoot[0];
  }

  try {
    const ancestry = await getFolderAncestry([folderId], 'folder');
    let remainingFolderIdsTillRoot: string[] = [];
    if (ancestry[folderId] && 'paths' in ancestry[folderId] && folderId in ancestry[folderId].paths && ancestry[folderId].paths[folderId].length > 0 && ancestry[folderId].paths[folderId][0].length > 0) {
      remainingFolderIdsTillRoot = ancestry[folderId]['paths'][folderId][0].map(x => x.id) || [];
    }

    if (!includeTenantSpace) {
      // Get rid of 'Tenant Space'
      remainingFolderIdsTillRoot.shift();
    }
    remainingFolderIdsTillRoot = remainingFolderIdsTillRoot.concat(folderIdsTillRoot);
    return remainingFolderIdsTillRoot;
  } catch (err) {
    logErrorAndShowToast('Failed to get folder id to root', err, 'ex in getFolderIdsRoRoot');
    return [];
  }
}


export const getPathToRootAndFetchSubfolders = async (folderId: string, includeTenantSpace = false) => {
  if (!folderId) return [];
  const path = await getFolderIdsToRoot(folderId, includeTenantSpace);
  await fetchAllSubfoldersFromIdList(path);
  return path;
};

export const getLastFolderFromPathStringAndFetchSubfolders = async (folderPath: (string | undefined), includeTenantSpace = false) => {
  if (!folderPath) return null;
  const folders = folderPath.split('/');

  let subTreeToSearch = includeTenantSpace
    ? getTreeIncludingRoot(foldersGlobal.rootId, foldersGlobal.tree)
    : getTree(foldersGlobal.rootId, foldersGlobal.tree);
  for (const folder of folders) {
    if (folder) {
      const folderViewModel = subTreeToSearch.find((item) => item.name === folder);
      if (folderViewModel && folderViewModel.hasSubfolder && !folderViewModel.hasLoadedSubFolders) {
        await fetchSubfolders(folderViewModel, undefined);
      }
      subTreeToSearch = folderViewModel?.subTrees as FolderViewModel[] || [];
      if (folder == folders[folders.length - 1]) {
        return folderViewModel ?? null;
      }
    }
  }
  return null;
};
